import { useState, useEffect, useMemo, useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { type components } from "@njbsoft/types";
import { useTasks, type TasksListParams } from "@/hooks/use-tasks";
import { useTaskFilters, type TaskSortBy } from "@/hooks/use-task-filters";
import { TaskFilters } from "@/components/tasks/task-filters";
import { shouldIncludeSubtasks, taskDetailTargetId } from "./tasks-list-params";
import { useTasksColumns } from "@/components/tasks/tasks-columns";
import { TaskBoard } from "@/components/tasks/task-board";
import { TaskCreateDrawer } from "@/components/tasks/task-create-drawer";
import { useAppStore } from "@/store";
import { PageHeader, Button, Card, DataTable, EmptyState, SkeletonTable } from "@/components/ui";

// URL-seeded search params: a deep link names either a person row
// (`assigned_to`) or a group row (`assigned_to_group`). They seed the
// persisted assignee/group filter once on mount (transient filter state
// otherwise lives in the per-org localStorage store).
interface TasksListSearch {
  assigned_to?: string;
  assigned_to_group?: string;
}

export const Route = createFileRoute("/_authenticated/tasks/")({
  component: TasksListPage,
  validateSearch: (s: Record<string, unknown>): TasksListSearch => ({
    assigned_to: typeof s.assigned_to === "string" ? s.assigned_to : undefined,
    assigned_to_group: typeof s.assigned_to_group === "string" ? s.assigned_to_group : undefined,
  }),
});

const PAGE_SIZE = 50;

/**
 * Translates the persisted filter state into the list query's shared params.
 * The page owns pagination/sort; the board reuses the same shared filter set.
 */
function buildSharedParams(filters: ReturnType<typeof useTaskFilters>["filters"]): TasksListParams {
  return {
    search: filters.search,
    status: filters.status,
    priority: filters.priority,
    assigned_to: filters.assignee,
    assigned_to_group: filters.group,
    me: filters.myTasks || undefined,
    label: filters.label,
    due_after: filters.dueFrom,
    due_before: filters.dueTo,
    overdue: filters.overdue || undefined,
  };
}

/**
 * Org task list page (Refined SAMS). A segmented List|Board toggle shares one
 * persisted, per-org filter state (search · status · priority · assignee ·
 * group · My-Tasks · due window · overdue · label). The List view is
 * server-paged with a "Showing X–Y of N" footer; the Board view fans the same
 * filters out into four status-pinned columns with summary-driven counts.
 *
 * Sort columns are backed by the server ORDER BY allow-list (title / status /
 * priority / due_date); the chosen sort persists with the rest of the filters.
 */
function TasksListPage() {
  const { t } = useTranslation(["tasks", "common"]);
  const navigate = useNavigate();
  const orgId = useAppStore((s) => s.activeOrgId);

  const { filters, setFilter, clearFilter, resetFilters } = useTaskFilters(orgId ?? "");

  // Seed the assignee/group filter from the URL once on mount — it overrides
  // the persisted filter state only at first render.
  const { assigned_to: drillAssignee, assigned_to_group: drillGroup } = Route.useSearch();
  useEffect(() => {
    if (drillAssignee) {
      setFilter("assignee", drillAssignee);
      clearFilter("group");
    } else if (drillGroup) {
      setFilter("group", drillGroup);
      clearFilter("assignee");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only URL seeding
  }, []);

  const [view, setView] = useState<"list" | "board">("list");
  const [createOpen, setCreateOpen] = useState(false);
  const [offset, setOffset] = useState(0);

  // Sort is driven by the persisted sortBy/sortDir; default due_date asc.
  const sorting = useMemo<SortingState>(
    () => [{ id: filters.sortBy ?? "due_date", desc: filters.sortDir === "desc" }],
    [filters.sortBy, filters.sortDir],
  );

  const sharedParams = useMemo(() => buildSharedParams(filters), [filters]);

  // Reset to the first page whenever the filter set or sort changes.
  useEffect(() => {
    setOffset(0);
  }, [sharedParams, filters.sortBy, filters.sortDir]);

  const listParams = useMemo<TasksListParams>(
    () => ({
      ...sharedParams,
      // Subtasks only surface once the list is narrowed to one person's queue
      // (My Tasks or a specific assignee) — see tasks-list-params.ts. The
      // Kanban board (useTasksByStatus in use-tasks.ts) always stays
      // top-level, regardless of this filter state.
      top_level_only: !shouldIncludeSubtasks({
        myTasks: filters.myTasks,
        assignee: filters.assignee,
      }),
      sort_by: filters.sortBy ?? "due_date",
      sort_dir: filters.sortDir ?? "asc",
      limit: PAGE_SIZE,
      offset,
    }),
    [sharedParams, filters.myTasks, filters.assignee, filters.sortBy, filters.sortDir, offset],
  );

  // Gate the list query to the List view — the Board runs its own per-status
  // queries, so the list query must not fire (and re-ship the wide list) while
  // the Board is active.
  const { data, isLoading, isError } = useTasks(listParams, { enabled: view === "list" });

  const openDetail = useCallback(
    (task: components["schemas"]["Task"]) => {
      void navigate({ to: "/tasks/$id", params: { id: taskDetailTargetId(task) } });
    },
    [navigate],
  );

  const columns = useTasksColumns({ onView: openDetail });

  const handleSortingChange = useCallback(
    (next: SortingState) => {
      const first = next[0];
      if (!first) return;
      setFilter("sortBy", first.id as TaskSortBy);
      setFilter("sortDir", first.desc ? "desc" : "asc");
    },
    [setFilter],
  );

  const handleViewAllCompleted = useCallback(() => {
    setFilter("status", "completed");
    setView("list");
  }, [setFilter]);

  const tasks = data?.items ?? [];
  const total = data?.total_count ?? 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  const hasFilters = useMemo(
    () =>
      !!filters.search ||
      !!filters.status ||
      !!filters.priority ||
      !!filters.assignee ||
      !!filters.group ||
      !!filters.label ||
      !!filters.dueFrom ||
      !!filters.dueTo ||
      !!filters.overdue ||
      filters.myTasks,
    [filters],
  );

  const segClass = (active: boolean) =>
    `h-8 rounded-md px-3 text-sm font-medium transition-colors ${
      active
        ? "bg-white text-on-surface shadow-sm dark:bg-gray-700 dark:text-white"
        : "text-muted hover:text-on-surface"
    }`;

  return (
    <div className="p-8">
      <div className="space-y-6">
        <PageHeader
          title={t("list.title", { ns: "tasks" })}
          actions={
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
                <button
                  type="button"
                  className={segClass(view === "list")}
                  onClick={() => setView("list")}
                >
                  {t("list.viewList", { ns: "tasks" })}
                </button>
                <button
                  type="button"
                  className={segClass(view === "board")}
                  onClick={() => setView("board")}
                  aria-label={t("board.title", { ns: "tasks" })}
                >
                  {t("board.view", { ns: "tasks" })}
                </button>
              </div>
              <Button onClick={() => setCreateOpen(true)}>
                {t("list.newTask", { ns: "tasks" })}
              </Button>
            </div>
          }
        />

        <TaskFilters
          filters={filters}
          setFilter={setFilter}
          clearFilter={clearFilter}
          resetFilters={resetFilters}
        />

        {view === "board" ? (
          <TaskBoard sharedFilters={sharedParams} onViewAllCompleted={handleViewAllCompleted} />
        ) : isLoading ? (
          <SkeletonTable columns={6} />
        ) : isError ? (
          <p className="text-sm text-danger">{t("errors.loadFailed", { ns: "tasks" })}</p>
        ) : tasks.length === 0 ? (
          <EmptyState
            title={
              hasFilters
                ? t("list.emptyFiltered", { ns: "tasks" })
                : t("list.empty", { ns: "tasks" })
            }
            {...(!hasFilters && {
              actionLabel: t("list.newTask", { ns: "tasks" }),
              onAction: () => setCreateOpen(true),
            })}
          />
        ) : (
          <Card>
            <DataTable
              columns={columns}
              data={tasks}
              manualSorting
              sorting={sorting}
              onSortingChange={handleSortingChange}
              onRowClick={openDetail}
            />
            <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-xs text-muted dark:border-gray-700">
              <span>{t("pagination.showing", { ns: "tasks", from, to, total })}</span>
              <div className="inline-flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={!canPrev}
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                  className="rounded-md border border-gray-200 px-2.5 py-1 text-xs text-on-surface hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  {t("pagination.previous", { ns: "tasks" })}
                </button>
                <button
                  type="button"
                  disabled={!canNext}
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                  className="rounded-md border border-gray-200 px-2.5 py-1 text-xs text-on-surface hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  {t("pagination.next", { ns: "tasks" })}
                </button>
              </div>
            </div>
          </Card>
        )}

        <TaskCreateDrawer open={createOpen} onClose={() => setCreateOpen(false)} />
      </div>
    </div>
  );
}
